import type { GameAction, ReduceContext } from './core/actions';
import { officialConfig, type RoomConfig } from './core/config';
import type { GameEvent } from './core/events';
import { playerId, type CardTitle, type CardUid, type PlayerId } from './core/ids';
import { cardsUsedBy, options, type Option } from './core/options';
import { project, type PlayerView } from './core/project';
import { reduce } from './core/reducer';
import { createLobbyState, validateConfig } from './core/setup';
import type { GameState } from './core/state';
import type { CardRegistry } from './core/pack';
import { defaultRegistry } from './packs';
import { planHostTimeout } from './host/rules';
import { nextU32 } from './core/rng';
import {
  BOT_PROFILES,
  botActor,
  chooseBotAction,
  type BotProfile,
} from './bot/policy';
import { t } from './i18n';
import { cardName } from './core/cardText';

/**
 * **热座（同屏轮流）控制器**。
 *
 * ⚠️ 这份实现**原样来自归档**（`archive/hotseat-ui-v1/src/hotseat.ts`）——
 * 它当年是对的，而且它同时是**联机层的雏形**，所以没有理由重写。
 * 这一轮接的是界面，不是它。
 *
 * 一个进程里跑完整的 `GameState`，把「操作者」当成一个可切换的指针。
 * 这不是权宜之计 —— 它同时是**联机层的雏形**：
 *
 * - UI 只吃 `PlayerView`（`project` 的输出），**从不碰 `GameState`**
 * - `actor` 就是「这个视图是谁的」，联机时改成从网络来即可
 * - `act()` 是唯一入口，联机时改成「发消息给房主、等新房主状态」即可
 *
 * 换句话说，热座就是把网络换成"手动切座位"的同一个模型。
 * 这样第 9 步做棋盘时不用为联机再改一次（`architecture.md` §2.2）。
 */
export class HotSeat {
  private state: GameState;
  private readonly ctx: ReduceContext;
  private readonly listeners = new Set<() => void>();
  private readonly eventListeners = new Set<(events: readonly GameEvent[]) => void>();

  /** `null` = 自动跟随（轮到谁就是谁）；指定某人 = 锁定成他 */
  private operator: PlayerId | null = null;

  /**
   * **暂时把视角钉在谁身上**（`null` = 没钉）。
   *
   * 和 `operator` 是两件事：`operator` 是"玩家自己锁的座位"，这个是
   * **动画**按住的 —— 抽牌动画播放期间不能把设备交出去，否则那张牌会飞进
   * 下一个人的手里（评审：「牌飞入手牌……并没有移动到其准确位置」）。
   *
   * `act()` 会清 `operator`，但**不清它**：那是动画的事，动画结束自己放掉。
   */
  private hold: PlayerId | null = null;

  /** 最近发生的、值得给玩家看的消息（新的在后） */
  private messages: string[] = [];

  /**
   * **房主时序计划**（`planHostTimeout` 的输出）。`null` = 现在不需要定时。
   *
   * 热座没有 Session 的 `setTimeout`，但「到点关掉打断窗口」这件事必须有人做 ——
   * 否则打出的牌会永远停在画面正中。见 `tick()`。
   */
  private timeoutPlan: { readonly afterMs: number; readonly action: GameAction } | null = null;
  /** 距离上一次排计划之后过了多久（毫秒）。每帧 `tick` 累加 */
  private timeoutElapsedMs = 0;

  /** 哪些座位由机器人代打（默认没有） */
  private readonly bots: Set<PlayerId>;
  /** 有机器人时，UI 想一直保持的真人视角（`null` = 正常跟随） */
  private viewer: PlayerId | null = null;
  /** 机器人的随机源：种子化，所以整局可复现 */
  private rngState: { seed: number };
  private botElapsedMs = 0;
  private readonly botProfile: BotProfile = BOT_PROFILES[0] as BotProfile;

  constructor(
    state: GameState,
    ctx: ReduceContext,
    bots: readonly PlayerId[] = [],
    seed = 1,
  ) {
    this.state = state;
    this.ctx = ctx;
    this.bots = new Set(bots);
    this.rngState = { seed: seed >>> 0 };
    this.rearmTimeout();
  }

  // ============================================================
  // 只读
  // ============================================================

  get fullState(): GameState {
    return this.state;
  }

  /**
   * **某个 uid 是哪张牌。**
   *
   * ## ⚠️ 这是"UI 只吃 `PlayerView`"那条纪律上的一个**故意的小口子**
   *
   * 只有一个调用点：抽牌动画（`ui/screens/game.ts` 的 `defOfUid`）。理由很具体 ——
   * 热座在**抽完的同一瞬间**就把操作者交给了下一个人（`actor` 是自动跟随当前
   * 回合的），于是"刚抽到的那张牌"立刻从新操作者的视图里消失了：
   * 它在**上一个人**手里，而那个人此刻没有任何一份视图。
   *
   * 而那段动画的设计就是要把这张牌亮出来（评审：「将牌背揭露为牌面的动画」），
   * 所以它必须有个地方能问到牌名。这里只回答**牌名这一个问题** ——
   * 不返回手里还有些什么、也不返回牌堆顺序。
   *
   * `null` = 这个 uid 不属于这一局。
   */
  titleOf(uid: CardUid): CardTitle | null {
    return this.state.cards[uid]?.title ?? null;
  }

  /**
   * 现在轮到「谁来操作」。
   *
   * 优先级：手动锁定的 > 待抉择的人 > 当前回合的人 > 房主。
   *
   * ⚠️ 响应窗口里**默认是当前回合玩家**，不是出牌的人 ——
   * 因为窗口就在那个回合内，而且回合一结束窗口就该关了。
   * 想替别人打休想就点座位切换（这是热座必须有的操作）。
   */
  get actor(): PlayerId {
    // 动画按住的那个优先级最高：它按住的正是"刚才动过手的那个人"
    if (this.hold !== null && this.state.players.some((p) => p.id === this.hold)) {
      return this.hold;
    }
    if (this.operator !== null && this.state.players.some((p) => p.id === this.operator)) {
      return this.operator;
    }
    // ★ 机器人正在行动 → 视角保持在真人身上，别把他的牌亮出来
    if (this.viewer !== null) {
      const active = this.state.decision?.player ?? this.state.turn?.player;
      if (active !== undefined && this.bots.has(active)) return this.viewer;
    }
    if (this.state.decision !== null) return this.state.decision.player;
    if (this.state.turn !== null) return this.state.turn.player;
    return this.state.host;
  }

  /** 这个操作者被锁定了没有（UI 用它显示「自动跟随」还是「替某人」） */
  get locked(): boolean {
    return this.operator !== null;
  }

  /** 操作者能看到的视图。UI 唯一的数据来源 */
  get view(): PlayerView {
    return project(this.state, this.actor);
  }

  /** 操作者当前所有**真的会改变局面**的合法动作 */
  get options(): readonly Option[] {
    return options(this.state, this.actor, this.ctx);
  }

  get log(): readonly string[] {
    return this.messages;
  }

  /**
   * **房主时序的现场读数**（诊断用）：现在排着的计划、已经等了多久。
   *
   * ⚠️ 只读。它不参与任何判断 —— 判断永远走 planHostTimeout。
   */
  get timeout(): { afterMs: number; action: GameAction; elapsedMs: number } | null {
    const plan = this.timeoutPlan;
    if (plan === null) return null;
    return { afterMs: plan.afterMs, action: plan.action, elapsedMs: this.timeoutElapsedMs };
  }

  // ============================================================
  // 写
  // ============================================================

  /**
   * 执行一个动作。
   *
   * 返回 `false` 表示被 reducer 拒了，`this.rejectReason` 里有原因。
   * 被拒**不抛异常** —— 和 reducer 保持一致。
   */
  act(action: GameAction): boolean {
    const result = reduce(this.state, action, this.ctx);
    if (!result.ok) {
      this.rejectReason = result.reason;
      this.emit();
      return false;
    }
    this.state = result.state;
    this.rejectReason = null;
    this.appendMessages(result.events);
    for (const listener of [...this.eventListeners]) listener(result.events);

    // ⚠️ **锁定只在"有事要交接"期间有效。**
    // 一旦没有待抉择、也没有响应窗口，就自动恢复跟随当前回合玩家 ——
    // 否则设备会在"关窗"这种收尾动作之后留在房主手里，
    // 界面上就会出现"轮到 D 了但 D 一个按钮都没有"的假死。
    if (this.state.decision === null && this.state.resolution === null) {
      this.operator = null;
    }

    this.rearmTimeout();
    this.emit();
    return true;
  }

  rejectReason: string | null = null;

  /**
   * **理一下当前操作者的手牌** —— 随时可做（回合内外都行）。
   *
   * 走的是普通动作那条路（`hand/reorder` 在 reducer 里不做阶段检查），
   * 所以它会进事件流、能被同步出去。界面只管把"拖到哪一位"翻译过来。
   */
  reorder(from: number, to: number): boolean {
    return this.act({ t: 'hand/reorder', actor: this.actor, from, to });
  }

  /**
   * **把视角钉在某人身上（或放掉）。**
   *
   * 只有一个调用者：对局屏的抽牌动画 —— 牌从牌堆飞到手里这几百毫秒里，
   * 设备不能被交出去（交出去的话牌就飞进下一个人的手里了）。动画一结束就
   * `holdActor(null)`。
   *
   * 和目标值一样时**什么都不做**（不 `emit()`）：界面每帧都会调它一遍，
   * 每次都通知订阅者的话就是每帧重建一次。
   */
  holdActor(id: PlayerId | null): void {
    if (this.hold === id) return;
    if (id !== null && !this.state.players.some((p) => p.id === id)) return;
    this.hold = id;
    this.emit();
  }

  /** 现在钉在谁身上（诊断用） */
  get heldActor(): PlayerId | null {
    return this.hold;
  }

  // ============================================================
  // 时钟（热座的「房主定时器」）
  // ============================================================

  /**
   * **每帧推一次时钟。** 到点了就按房主时序自动发那个动作。
   *
   * 联机那边由 `S3Session` 用 `setTimeout` 干这件事；热座是同进程，
   * 所以挂在唯一那个 ticker 上（见 `ui/screens/game.ts` 的 `updateFrame`）。
   * ⚠️ 它和联机共用 `planHostTimeout`，截止时间不会漂。
   */
  tick(deltaMs: number): void {
    // ★ 先让机器人动（它可以打断，也可以出牌）；人类操作者由 UI 负责
    this.tickBots(deltaMs);
    const plan = this.timeoutPlan;
    if (plan === null) return;
    this.timeoutElapsedMs += Math.max(0, deltaMs);
    if (this.timeoutElapsedMs < plan.afterMs) return;
    // ⚠️ 到点时局面可能已经变了 → 再问一遍，只有仍然要发才发（和 Session 一致）
    const again = planHostTimeout(this.state, this.state.host, this.ctx, (id) => this.bots.has(id));
    if (again === null) {
      this.timeoutPlan = null;
      return;
    }
    this.act(again.action);
  }

  /** 这个座位是机器人吗（UI 可以据此显示「思考中」） */
  isBot(id: PlayerId): boolean {
    return this.bots.has(id);
  }

  /**
   * **把视角钉在某个真人身上。** 有机器人时，轮到他行动而 UI 还跟着 turn 走的话，
   * 会把机器人的手牌亮给真人看。钉住之后 `actor` 在机器人行动期间保持返回他。
   */
  setViewer(id: PlayerId | null): void {
    this.viewer = id !== null && this.state.players.some((p) => p.id === id) ? id : null;
  }

  /** 机器人思考多久走一步（毫秒） */
  private static readonly BOT_THINK_MS = 450;

  private random(): number {
    const step = nextU32(this.rngState);
    this.rngState = step.state;
    return step.value / 4294967296;
  }

  private tickBots(deltaMs: number): void {
    if (this.bots.size === 0) return;
    const actor = botActor(this.state, this.ctx, () => this.random());
    if (actor === undefined || !this.bots.has(actor)) {
      this.botElapsedMs = 0;
      return;
    }
    this.botElapsedMs += Math.max(0, deltaMs);
    if (this.botElapsedMs < HotSeat.BOT_THINK_MS) return;
    this.botElapsedMs = 0;
    const action = chooseBotAction({
      state: this.state,
      actor,
      ctx: this.ctx,
      profile: this.botProfile,
      random: () => this.random(),
    });
    if (action !== undefined) this.act(action);
  }

  /** 重新问一遍时序策略。每次状态变化都调（和 Session 的 `armHostTimer` 同一套） */
  private rearmTimeout(): void {
    this.timeoutElapsedMs = 0;
    this.timeoutPlan = planHostTimeout(
      this.state,
      this.state.host,
      this.ctx,
      (id) => this.bots.has(id),
    );
  }

  /**
   * 锁定 / 解锁操作者。传 `null` 恢复自动跟随。
   *
   * ⚠️ **出局的人也可能需要操作** —— 尤其房主：`window/timeout` 只认房主，
   * 不看他是死是活。房主被炸掉之后，响应窗口仍然只有他能关，
   * 所以这里**不能**拿 `alive` 当门槛。界面层才负责灰掉没用的座位。
   *
   * 只对"不存在的玩家"说不 —— 那是真的打错字了。
   */
  setOperator(id: PlayerId | null): void {
    if (id !== null && !this.state.players.some((p) => p.id === id)) return;
    this.operator = id;
    this.emit();
  }

  /**
   * **现在必须由谁出面** —— 与当前操作者无关的"事实"。
   *
   * 两种情况：
   *
   * 1. 有人要做抉择 → 就是他
   * 2. 打断窗口开着 → **房主**（只有他能按动画时限关窗；谁都可能打断，
   *    但「必须出面」的只有负责推进的那一个）。
   *
   * 返回 `null` 表示"没有人被点名，轮到谁就是谁"。
   *
   * ⚠️ 这个函数**不改变** `actor`，也不做"是否等于当前操作者"的比较 ——
   * 那是调用方的事。UI 用它渲染「把设备交给 X」，由人决定什么时候换手；
   * 自动跳座位会把热座变成猜谜（而且下家的手牌会在设备还在上家手里时就亮出来）。
   */
  nextRequiredActor(): PlayerId | null {
    if (this.state.decision !== null) return this.state.decision.player;

    // 新模型：打断窗口里没有"必须表态的人"——谁都可能打断，
    // 但只有房主能按动画时限关窗。
    if (this.state.resolution !== null) return this.state.host;
    return null;
  }

  // ============================================================
  // 订阅
  // ============================================================

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * 订阅**原始事件**。
   *
   * ⚠️ 和 `subscribe` 是两件事：那个是"状态变了，重画"，
   * 这个是"刚刚发生了这些事，放动画"。表现层要的是后者 ——
   * 评审要的翻牌动画就是挂在 `card/stolen` 上的。
   */
  onEvents(listener: (events: readonly GameEvent[]) => void): () => void {
    this.eventListeners.add(listener);
    return () => {
      this.eventListeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of [...this.listeners]) listener();
  }

  private appendMessages(events: readonly GameEvent[]): void {
    for (const event of events) {
      const text = describeEvent(event, this.state, this.ctx.registry);
      if (text !== null) this.messages.push(text);
    }
    // 只留最近 40 条 —— 热座时没人会往回翻很久
    if (this.messages.length > 40) this.messages = this.messages.slice(-40);
  }
}

// ============================================================
// 开局
// ============================================================

export interface HotSeatSetup {
  readonly names: readonly string[];
  readonly seed: number;
  readonly config?: RoomConfig;
  readonly ctx?: ReduceContext;
  /** 哪些昵称由机器人代打（必须在 `names` 里）。不给 = 全是人 */
  readonly botNames?: readonly string[];
}

/**
 * 直接搭一局已经发好牌的热座对局。
 *
 * ⚠️ 走的是**和真实开局完全相同的动作序列**（`lobby/join` ×N → `game/start`），
 * 而不是手工拼一个 `GameState`。这样热座就不可能绕过校验或发牌规则 ——
 * 一旦 reducer 有 bug，这里立刻跟着坏，而不是"测试环境特供"地正常。
 */
export function createHotSeat(params: HotSeatSetup): HotSeat {
  const ctx = params.ctx ?? { registry: defaultRegistry };
  const count = params.names.length;
  const base = params.config ?? officialConfig(count as 2 | 3 | 4 | 5, defaultRegistry);

  const problems = validateConfig(base, defaultRegistry);
  if (!problems.ok) {
    throw new Error(
      t('audio.hotseat.configInvalid', {
        problems: problems.problems.map((p) => p.message).join(t('common.problemSeparator')),
      }),
    );
  }

  const host = playerId(params.names[0] ?? 'P1');

  // ⚠️ 开局动作**走 HotSeat 自己的 `act`**，而不是直接调 reducer。
  // 这样"加入 / 开局"这些事件也会进日志 —— 玩家一进游戏就能看到谁加了进来，
  // 而不是面对一块空白。同时保证热座没有一条绕过校验的旁路。
  const bots = (params.botNames ?? []).map((name) => playerId(name));
  const game = new HotSeat(createLobbyState({ host, config: base }), ctx, bots, params.seed);

  const run = (action: GameAction): void => {
    if (!game.act(action)) {
      throw new Error(
        t('audio.hotseat.startFailed', {
          action: action.t,
          reason: game.rejectReason ?? t('common.unknownReason'),
        }),
      );
    }
  };

  for (const name of params.names) {
    run({ t: 'lobby/join', actor: playerId(name), name });
  }
  run({ t: 'game/start', actor: host, seed: params.seed });

  return game;
}


// ============================================================
// 事件 → 人话
// ============================================================

/**
 * 把一条事件翻译成给玩家看的中文。
 *
 * ⚠️ 这里查不到牌名就退回 uid：事件里只有 uid，而**牌名要靠
 * 当时的状态**才查得到。所以调用方必须在 `state` 已经前进之后调用 ——
 * 好在那张牌这时通常已经在弃牌堆里了。
 *
 * 返回 `null` 表示这条事件不值得占用一行（UI 高频刷新时噪音太大）。
 */
export function describeEvent(
  event: GameEvent,
  state: GameState,
  registry?: CardRegistry,
): string | null {
  const who = (id: PlayerId): string =>
    state.players.find((p) => p.id === id)?.name ?? String(id);
  const card = (uid: number): string => {
    const title = state.cards[uid]?.title;
    if (title === undefined) return `#${uid}`;
    // 中文显示名更好读；查不到就退回**去掉命名空间**的局部名
    //（`vanilla:Await` → `Await`，而不是把前缀一起打给玩家看）
    const def = registry?.get(title);
    return def === undefined ? String(title).replace(/^[^:]+:/, '') : cardName(def);
  };

  switch (event.t) {
    case 'lobby/playerJoined':
      return t('audio.hotseat.event.playerJoined', { name: event.name });
    case 'lobby/playerLeft':
      return t('audio.hotseat.event.playerLeft', { name: who(event.player) });
    case 'lobby/readyChanged':
      return event.ready
        ? t('audio.hotseat.event.readyChangedReady', { name: who(event.player) })
        : t('audio.hotseat.event.readyChangedUnready', { name: who(event.player) });
    case 'lobby/configChanged':
      return t('audio.hotseat.event.configChanged', { name: who(event.by) });
    case 'game/started':
      return t('audio.hotseat.event.started');
    case 'game/finished':
      return t('audio.hotseat.event.finished', { name: who(event.winner) });
    case 'card/drawn':
      return t('audio.hotseat.event.drawn', { name: who(event.player) });
    case 'card/played':
      return t('audio.hotseat.event.played', {
        name: who(event.player),
        cards: event.cards.map(card).join(' + '),
      });
    case 'card/revealed':
      return t('audio.hotseat.event.revealed', { card: card(event.card) });
    case 'card/discarded':
      return t('audio.hotseat.event.discarded', { cards: event.cards.map(card).join(' + ') });
    case 'card/stolen':
      return t('audio.hotseat.event.stolen', { name: who(event.from) });
    case 'card/given':
      return t('audio.hotseat.event.given', { from: who(event.by), to: who(event.to) });
    case 'card/reclaimed':
      return t('audio.hotseat.event.reclaimed', {
        name: who(event.by),
        card: card(event.card),
      });
    case 'pile/shuffled':
      return t('audio.hotseat.event.shuffled');
    case 'pile/peeked':
      return t('audio.hotseat.event.peeked', { name: who(event.player), n: event.count });
    case 'lethal/drawn':
      return t('audio.hotseat.event.lethalDrawn', {
        name: who(event.player),
        card: card(event.card),
      });
    case 'lethal/defused':
      return t('audio.hotseat.event.lethalDefused', { name: who(event.player) });
    case 'lethal/exploded':
      return t('audio.hotseat.event.lethalExploded', { name: who(event.player) });
    case 'window/opened':
      return t('audio.hotseat.event.windowOpened', { name: who(event.by) });
    case 'window/nope':
      return t('audio.hotseat.event.windowNope', {
        name: who(event.player),
        card: card(event.card),
      });
    case 'window/closed':
      return event.cancelled
        ? t('audio.hotseat.event.windowClosedCancelled')
        : t('audio.hotseat.event.windowClosedApplied');
    case 'turn/started':
      return t('audio.hotseat.event.turnStarted', {
        name: who(event.player),
        n: event.turnsRemaining,
      });
    case 'turn/ended':
      return t('audio.hotseat.event.turnEnded', { name: who(event.player) });
    case 'turn/attacked':
      return t('audio.hotseat.event.turnAttacked', {
        name: who(event.player),
        n: event.turns,
      });
    default:
      return null;
  }
}

/** UI 用：把一次出牌涉及的手牌 uid 拿出来（选中态高亮 / 过滤按钮） */
export { cardsUsedBy };
